/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';

import styles from '../../styles';

const CheckboxInput = styled.input`
  width: 1px;
  height: 1px;
  border: 0;
  clip: rect(0 0 0 0);
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
`;

const FauxCheckbox = styled.span`
  width: 1rem;
  height: 1rem;
  display: inline-block;
  position: relative;
  vertical-align: middle;
  border: 1px solid #d8dde6;
  border-radius: 0.125rem;
  background: white;
  transition: border 0.1s linear, background-color 0.1s linear;

  margin-right: 0.5rem;

  /* If Radio Input is checked, then draw the radion dot
  in the next label's child FauxRadio's after area */
  ${CheckboxInput}:focus + label & {
    border-color: #1589ee;
    box-shadow: 0 0 3px #0070D2;
  }

  /* If Radio Input is checked, then draw the radion dot
  in the next label's child FauxRadio's after area */
  ${CheckboxInput}:checked + label &:after {
    display: block;
    content: '';
    height: 0.25rem;
    width: 0.5rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0) rotate(-45deg);
    border-bottom: 2px solid #1589ee;
    border-left: 2px solid #1589ee;
  }

  ${CheckboxInput}:indeterminate + label &:after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0.5rem;
    height: 2px;
    background: #1589ee;
    border: 0;
    transform: translate3d(-50%, -50%, 0);
  }

  ${CheckboxInput}[disabled] + label & {
    background-color: #ecebea;
    border-color: #c9c7c5;
  }

  ${CheckboxInput}[disabled] + label &:after {
    border-color: white;
  }
`;

const Label = styled.span`
  display: inline;

  vertical-align: middle;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #706e6b;

  margin-bottom: 0.125rem;
`;

class CheckboxComponent extends React.Component {
  constructor(props) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.ref = this.ref.bind(this);
  }

  onChange(event) {
    const value = event.target.value;
    if (this.props.onChange) this.props.onChange(event.target.checked);
  }

  ref(input) {
    // React does not recognize the `indeterminate` attribute so it must be manually assigned
    // to the DOM
    if (input) {
      this.input = input;
      this.input.indeterminate = this.props.indeterminate;
    }
  }

  UNSAFE_componentWillReceiveProps (nextProps) {
    if (this.input) this.input.indeterminate = nextProps.indeterminate;
  }

  render() {
    const { id, name, text, value, className, checked, disabled, indeterminate } = this.props;

    return (
      <span className={className}>
        <CheckboxInput
          type='checkbox'
          checked={checked}
          id={id || name}
          name={name}
          value={value}
          onChange={this.onChange}
          disabled={Boolean(disabled)}
          innerRef={this.ref}
        />
        <label htmlFor={id || name}>
          <FauxCheckbox active={true} />
          <Label>{text}</Label>
        </label>
      </span>
    );
  }
}

export default CheckboxComponent;
